import { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import "./user.css";
import CurrentPlan from './planes/userPlan/currentPlan';
import PastPlans from './planes/userPlan/pastPlans';
import { Button, Typography } from 'antd';
import apiClient from '../apis/ApiInterceptor';
import ScheduledSessions from './sessions/scheduledSessions';
import PastSessions from './sessions/pastSessions';
import Subscription from '../components/Subscription';
import UserBasicInformation from '../components/user/UserBasicInformation';
import Features from '../enums/features';
import { fetchAttendedSessionsCount } from '../apis/sessionsApi';
import { fetchAchievementProgressByName } from '../apis/achievementsApi';
import Achievements from '../enums/achievements';
import { checkAutoPermissions, isFeatureEnabledForBranch } from '../apis/featureApi';
import EditUserModal from '../components/EditUserModal';

const { Title } = Typography;

const User = () => {
  const { user_id } = useParams(); // Extraer el parámetro `user_id` de la URL.
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // State to trigger refresh
  const [classesCount, setClassesCount] = useState(0);
  const [rankingPosition, setRankingPosition] = useState(null);
  const [isUsersSensitiveInfoFeature, setIsUsersSensitiveInfoFeature] = useState(false);
  const [isRankingEnabled, setIsRankingEnabled] = useState(false);
  const [isSessionMilestonesEnabled, setIsSessionMilestonesEnabled] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isAuthorizedToEditProfile, setIsAuthorizedToEditProfile] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const userResponse = await apiClient.get(`/users/${user_id}`);
        if (userResponse.status !== 200) {
          throw new Error("Usuario no encontrado");
        }
        const foundUser = userResponse.data;
        setUser(foundUser);
        const [attendedSessionsResponse, rankingResponse] = await Promise.all([
          fetchAttendedSessionsCount(user_id),
          fetchAchievementProgressByName(Achievements.RANKING),
        ]);

        if (attendedSessionsResponse && attendedSessionsResponse.status === 200) {
          setClassesCount(attendedSessionsResponse.data);
        }

        const rankingList = rankingResponse || [];
        // compute positions (ties share the same position)
        const sorted = [...rankingList].sort((a, b) => b.points - a.points);
        let lastPoints = null;
        let lastPosition = 0;
        const rankingWithPosition = sorted.map((item, idx) => {
          if (item.points !== lastPoints) {
            lastPosition = idx + 1;
          }
          lastPoints = item.points;
          return { ...item, position: lastPosition };
        });

        const userRow = rankingWithPosition.find(r => String(r.user_id) === String(user_id));
        setRankingPosition(userRow ? userRow.position : null);

        const isAuthorized = await checkAutoPermissions(Features.ADMIN_USER_EDITION, user_id);
        if (!isAuthorized) {
            const features = JSON.parse(localStorage.getItem('user_features') || '{}');
            setIsAuthorizedToEditProfile(features[Features.ADMIN_USER_EDITION] || false);
        }else{
            setIsAuthorizedToEditProfile(true);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user_id]);

  useEffect(() => {
    const features = JSON.parse(localStorage.getItem('user_features') || '{}');
    const usersSensitiveInfoFeature = features[Features.USERS_SENSITIVE_INFO] || false;
    setIsUsersSensitiveInfoFeature(usersSensitiveInfoFeature);
    setIsRankingEnabled(features[Features.RANKING] || false);
    loadBranchFeatures();
    async function loadBranchFeatures() {
      const sessionMilestonesFeature = await isFeatureEnabledForBranch(Features.SESSION_MILESTONES);
      setIsSessionMilestonesEnabled(sessionMilestonesFeature);
    }
  }, []);

  if (loading) return <p>Cargando...</p>;
  if (error) return <p>Error: {error}</p>;
  return (
    <div className='mobile-block centered-container m-auto'>
      <img src={`/img/avatars/${user.photo}`} alt="User Avatar" className="user-profile-icon"/>
      <div className="user-name-row">
        <Title level={3} style={{margin:0}}>{user.name} {user.surname_1} {user.surname_2}</Title>
        { isRankingEnabled &&
          <div className="ranking-badge" title="Posición en ranking mensual">{rankingPosition ? `# ${rankingPosition}` : '—'}</div>
        }
      </div>
      {/* Badge showing number of classes the user has taken */}
      { isSessionMilestonesEnabled &&
        <div className="user-classes-badge" aria-label="Clases tomadas">
          <span className="badge-count">{classesCount}</span>
          <span className="badge-label">clases</span>
        </div>
      }

      { isAuthorizedToEditProfile &&
        <div className="my-3">
          <Button className='themed-btn' onClick={() => setShowEditModal(true)}>
            Editar Mi Perfil
          </Button>
          <EditUserModal
            visible={showEditModal}
            onClose={() => { setShowEditModal(false);}}
            user={user}
            onUpdated={(updatedUser) => {
              setUser(updatedUser);
              setShowEditModal(false);
            }}
          />
        </div>
      }

      <UserBasicInformation user={user}/>
      
      {/* TODO: move to a separate component once it have more sensitive information */}
      { (isUsersSensitiveInfoFeature && user?.pathology) &&
        <div>
          <p><span>Patologías:</span></p>
          <p><span>{`${user.pathology}`}</span></p>
        </div>
      }
      <ScheduledSessions userId={user.id} className="mt-3" onSessionCancel={() => setRefreshKey((prev) => prev + 1)} />
      <CurrentPlan userId={user.id} className="mt-3" refreshKey={refreshKey} setRefreshKey={setRefreshKey} />
      <Subscription userId={user.id} className="mt-3" />
      <PastPlans userId={user.id} className="mt-3" refreshKey={refreshKey} />
      <PastSessions userId={user.id} className="mt-3" refreshKey={refreshKey} />
    </div>
  );
};

export default User;
